<template>
  <div class="date-range-picker-wrapper">
    <VueDatePicker
      ref="datepicker"
      v-model="internalRange"
      range
      model-type="yyyy-MM-dd"
      :min-date="minDate"
      :max-date="maxDate"
      :enable-time-picker="false"
      :auto-apply="false"
      :locale="enGB"
      :week-start="WeekStart.Monday"
      :format="formatTrigger"
      :action-row="{
        selectBtnLabel: 'Apply',
        cancelBtnLabel: 'Cancel'
      }"
      :disabled="disabled"
      menu-class-name="date-range-menu"
    >
      <template #trigger>
        <button
          type="button"
          class="trigger-btn"
          :disabled="disabled"
          :aria-label="`Select period, current: ${triggerLabel}`"
        >
          <svg class="trigger-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="2"/>
            <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span class="trigger-label">{{ triggerLabel }}</span>
          <svg class="trigger-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </template>

      <template #left-sidebar>
        <DateRangePresets :selected="activePresetKey" @select="onPresetSelect" />
      </template>
    </VueDatePicker>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { VueDatePicker, WeekStart } from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { enGB } from 'date-fns/locale'
import DateRangePresets from './DateRangePresets.vue'
import { PRESETS, presetToRange, toIsoDate, formatRangeLabel } from '@/Modules/utils/dateRange.js'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  today: {
    type: String,
    default: () => toIsoDate(new Date())
  }
})

const emit = defineEmits(['update:modelValue'])

const datepicker = ref(null)

const minDate = new Date(2023, 0, 1)
const maxDate = new Date()

const internalRange = computed({
  get() {
    if (!props.modelValue?.from || !props.modelValue?.to) return null
    return [props.modelValue.from, props.modelValue.to]
  },
  set(value) {
    if (!value || value.length !== 2) return
    emit('update:modelValue', { from: value[0], to: value[1] })
  }
})

const triggerLabel = computed(() => {
  if (!props.modelValue?.from || !props.modelValue?.to) return 'Select period'
  return formatRangeLabel(props.modelValue, props.today)
})

const activePresetKey = computed(() => {
  if (!props.modelValue?.from || !props.modelValue?.to) return null
  for (const { key } of PRESETS) {
    const r = presetToRange(key, props.today)
    if (r && r.from === props.modelValue.from && r.to === props.modelValue.to) {
      return key
    }
  }
  return null
})

function formatTrigger() {
  return triggerLabel.value
}

function onPresetSelect(presetKey) {
  if (presetKey === 'custom') return
  const range = presetToRange(presetKey, props.today)
  if (!range) return
  emit('update:modelValue', range)
  datepicker.value?.closeMenu?.()
}
</script>

<style scoped>
.date-range-picker-wrapper {
  display: inline-flex;
  align-items: center;
}

.trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  color: var(--color-text);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 220px;
}

.trigger-btn:hover:not(:disabled) {
  color: var(--highlights);
  border-color: rgba(123, 24, 28, 0.2);
  box-shadow: 0 4px 12px rgba(123, 24, 28, 0.12);
}

.trigger-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.trigger-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.trigger-icon {
  flex-shrink: 0;
  color: var(--highlights);
  opacity: 0.75;
  transition: opacity 0.2s ease;
}

.trigger-btn:hover:not(:disabled) .trigger-icon {
  opacity: 1;
}

.trigger-label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
}

.trigger-chevron {
  flex-shrink: 0;
  opacity: 0.5;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.trigger-btn:hover:not(:disabled) .trigger-chevron {
  opacity: 1;
}
</style>

<style>
/* Global overrides for the popover — :deep() can't reach a teleported menu */
.dp__menu.date-range-menu {
  border-radius: var(--border-radius-card) !important;
  border: 1px solid var(--color-border) !important;
  box-shadow: var(--shadow-card-hover) !important;
  font-family: 'Montserrat', 'Segoe UI', 'Arial', sans-serif !important;
  overflow: hidden;
}

.dp__menu.date-range-menu .dp__action_buttons {
  gap: 8px;
}

.dp__menu.date-range-menu .dp__action_button {
  border-radius: 8px;
  padding: 6px 14px;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.15s ease;
}

.dp__menu.date-range-menu .dp__action_select {
  background: var(--highlights);
  border-color: var(--highlights);
}

.dp__menu.date-range-menu .dp__action_select:hover {
  background: #5e1316;
  border-color: #5e1316;
}

.dp__menu.date-range-menu .dp__today {
  border-color: var(--highlights);
}

.dp__menu.date-range-menu .dp__range_start,
.dp__menu.date-range-menu .dp__range_end,
.dp__menu.date-range-menu .dp__active_date {
  background: var(--highlights);
  color: #fff;
}

.dp__menu.date-range-menu .dp__range_between {
  background: var(--highlights-background);
  color: var(--highlights);
}

.dp__menu.date-range-menu .dp__cell_inner:not(.dp__active_date):not(.dp__range_start):not(.dp__range_end):not(.dp__range_between):hover {
  background: var(--highlights-hover);
  color: var(--highlights);
}
</style>
